// src/features/radar/RadarPage.jsx
//
// RADAR-BRIEF-STACK: Decision Brief Stack layout for the full-page Radar view.
//
// Layout: 50/50 two-column panel.
//   Left rail  — Three band sections: Act now / This cycle / Keep watch,
//                each with a count badge and items in score order.
//   Right pane — BriefMemo executive memo for the currently selected brief.
//
// Data:
//   Open mode     — GET /api/focus-items/feed  → server-ranked + banded items
//   Resolved mode — (toggle present; resolved list stays empty for now)
//
// The compact Radar pane on Pulse home (LeadershipRadar.jsx) is untouched
// and still calls getRadarFeed.

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { T } from "../../app/tokens.js";
import { getFocusItemsFeed, resolveFocusItem } from "../../data/focusItemsClient.js";
import { log } from "../../utils/logger.js";
import { BriefCard } from "./BriefCard.jsx";
import { BriefMemo } from "./BriefMemo.jsx";

// ─── Constants ────────────────────────────────────────────────────────────────

const MODE_OPEN     = "open";
const MODE_RESOLVED = "resolved";

// Band display configuration — order matters (act_now first)
const BANDS = [
  { key: "act_now",    label: "Act now",    dotColor: T.radarIssue  },
  { key: "this_cycle", label: "This cycle", dotColor: T.radarExpiry },
  { key: "keep_watch", label: "Keep watch", dotColor: T.mid         },
];

// ─── Normalise a focus_items row into the shape BriefCard/BriefMemo expect ───

function normaliseFocusItem(item) {
  return {
    ...item,
    // Stable UI selection key — prefer dedupe_key so it survives re-emits
    itemKey:  item.dedupe_key ?? item.id,
    // DB UUID preserved for API mutation calls (resolve route uses this)
    _dbId:    item.id,
    // Due date as YYYY-MM-DD string for BriefCard / radarUtils helpers
    dueDate:  item.due_at ? item.due_at.slice(0, 10) : null,
    // impact = server-computed score (numeric)
    impact:   item.score ?? 0,
    // owner from owner_id (focus_items stores the user email/id there)
    owner:    item.owner_id ?? null,
    // resolved state for BriefMemo toggle
    resolved: item.state === "resolved",
  };
}

// ─── RadarPage ────────────────────────────────────────────────────────────────

export function RadarPage({ onNavigate, currentRole: roleProp }) {
  const [userRole,    setUserRole]    = useState(roleProp ?? "viewer");
  const [feed,        setFeed]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);
  const [mode,        setMode]        = useState(MODE_OPEN);
  const [triageError, setTriageError] = useState(null);
  const [reloadKey,   setReloadKey]   = useState(0);

  const resolvedMode = mode === MODE_RESOLVED;

  // Role detection — prop wins; otherwise fetch /api/me
  useEffect(() => {
    if (roleProp !== undefined) return;
    fetch("/api/me", { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((u) => { if (u?.role) setUserRole(u.role); })
      .catch(() => {});
  }, [roleProp]);

  // Feed load
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    // Pass resolved flag so the server filters by state correctly
    getFocusItemsFeed({ resolved: resolvedMode, sort: resolvedMode ? null : "priority" })
      .then((data) => {
        if (cancelled) return;
        // Normalise all items
        const normalised = (data?.items ?? []).map(normaliseFocusItem);
        setFeed({ ...data, items: normalised });
        setLoading(false);
        if (normalised.length) {
          setSelectedKey((prev) => {
            const stillExists = normalised.some((i) => i.itemKey === prev);
            return stillExists ? prev : normalised[0].itemKey;
          });
        } else {
          setSelectedKey(null);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        log.error("RadarPage: feed load failed", { err: String(err) });
        setError(err);
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [reloadKey, resolvedMode]);

  // Items already arrive server-sorted by score DESC; preserve that order
  const allItems = useMemo(() => feed?.items ?? [], [feed]);

  // Group items by band, preserving server score order within each band
  const bandedItems = useMemo(() => {
    const map = {};
    for (const b of BANDS) map[b.key] = [];
    for (const item of allItems) {
      const key = item.band ?? "keep_watch";
      if (map[key]) map[key].push(item);
      else map["keep_watch"].push(item);
    }
    return map;
  }, [allItems]);

  const totalCount   = allItems.length;
  const selectedItem = allItems.find((i) => i.itemKey === selectedKey) ?? null;
  const isEditor     = userRole === "editor" || userRole === "admin";

  const handleSelect = useCallback((key) => {
    setSelectedKey(key);
    setTriageError(null);
  }, []);

  // Resolve via focus-items route.
  // Only "resolved: true" is supported — assign/snooze belong to a future task.
  // Uses item._dbId (UUID) for the API call, not the display itemKey.
  const handleTriage = useCallback(async (itemKey, patch) => {
    setTriageError(null);

    // Only act on resolve; ignore unsupported patches (e.g. assignedTo)
    if (!patch.resolved) return;

    // Look up the DB id for the API call
    const target = (feed?.items ?? []).find((i) => i.itemKey === itemKey);
    if (!target) return;
    const dbId = target._dbId;

    // Optimistic removal — item leaves the open list immediately
    setFeed((prev) => {
      if (!prev) return prev;
      return { ...prev, items: prev.items.filter((i) => i.itemKey !== itemKey) };
    });
    setSelectedKey((prev) => {
      if (prev !== itemKey) return prev;
      const remaining = (feed?.items ?? []).filter((i) => i.itemKey !== itemKey);
      return remaining.length ? remaining[0].itemKey : null;
    });

    try {
      await resolveFocusItem(dbId, patch.resolution ?? null);
      setReloadKey((n) => n + 1);
    } catch (err) {
      setReloadKey((n) => n + 1);
      setTriageError(err.message ?? "Action failed.");
    }
  }, [feed]);

  const handleModeSwitch = useCallback((newMode) => {
    setMode(newMode);
    setSelectedKey(null);
    setTriageError(null);
  }, []);

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div
      data-testid="radar-page"
      style={{ display: "flex", flexDirection: "column", gap: 14 }}
    >
      {/* ── Page heading row ── */}
      <div style={{
        display:        "flex",
        alignItems:     "center",
        justifyContent: "space-between",
        flexWrap:       "wrap",
        gap:            10,
      }}>
        <div>
          <h1 style={{
            margin:     0,
            fontSize:   T.fsLg,
            fontWeight: T.fwBold,
            color:      T.dark,
            lineHeight: 1.2,
          }}>
            Leadership Radar
          </h1>
          <p style={{ margin: "3px 0 0", fontSize: T.fsSm, color: T.light }}>
            What needs your attention, by when, and who owns it.
          </p>
        </div>

        {/* Open / Resolved segmented toggle */}
        <div
          role="group"
          aria-label="View mode"
          style={{
            display:      "inline-flex",
            background:   "var(--v2-hair)",
            borderRadius: 10,
            padding:      3,
            gap:          2,
            flexShrink:   0,
          }}
        >
          {[
            { key: MODE_OPEN,     label: mode === MODE_OPEN     && totalCount > 0 ? `Open (${totalCount})`     : "Open" },
            { key: MODE_RESOLVED, label: mode === MODE_RESOLVED && totalCount > 0 ? `Resolved (${totalCount})` : "Resolved" },
          ].map(({ key, label }) => {
            const active = mode === key;
            return (
              <button
                key={key}
                type="button"
                data-testid={`radar-mode-${key}`}
                onClick={() => handleModeSwitch(key)}
                aria-pressed={active}
                style={{
                  border:       0,
                  height:       T.toolbarSegH,
                  padding:      "0 14px",
                  borderRadius: 8,
                  fontWeight:   T.fwBold,
                  fontSize:     T.fsSm,
                  cursor:       "pointer",
                  whiteSpace:   "nowrap",
                  background:   active ? "var(--v2-card)"   : "transparent",
                  color:        active ? "var(--v2-accent)" : "var(--v2-mid)",
                  boxShadow:    active ? "0 1px 4px rgba(16,36,58,.1)" : "none",
                  transition:   "all .15s",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Two-column Decision Brief Stack ── */}
      <div style={{
        background:          T.white,
        border:              `1px solid ${T.border}`,
        borderRadius:        T.radiusCard,
        overflow:            "hidden",
        display:             "grid",
        gridTemplateColumns: "1fr 1fr",
        minHeight:           520,
        maxHeight:           "calc(100vh - 220px)",
      }}>
        {/* ── Left rail: band-grouped brief card stack ── */}
        <div style={{
          borderRight:   `1px solid ${T.border}`,
          display:       "flex",
          flexDirection: "column",
          overflow:      "hidden",
        }}>
          {/* Rail header */}
          <div style={{
            display:        "flex",
            alignItems:     "center",
            justifyContent: "space-between",
            padding:        "10px 14px",
            borderBottom:   `1px solid ${T.lightGray}`,
            flexShrink:     0,
            background:     T.lightGray,
          }}>
            <span style={{
              fontSize:   T.fsSm,
              fontWeight: T.fwBold,
              color:      T.dark,
            }}>
              Priority briefs
            </span>
            <button
              type="button"
              data-testid="radar-sort-control"
              style={{
                background:   T.white,
                border:       `1px solid ${T.border}`,
                borderRadius: T.radiusSm,
                padding:      "3px 8px",
                fontSize:     T.fsXs,
                color:        T.mid,
                cursor:       "default",
                fontWeight:   T.fwSemibold,
              }}
              disabled
            >
              Ranked by engine ▾
            </button>
          </div>

          {/* Card list — band-grouped */}
          <div style={{ overflowY: "auto", flex: 1, padding: "8px 10px", display: "flex", flexDirection: "column", gap: 0 }}>
            {loading && (
              <div style={{ padding: 40, color: T.light, fontSize: T.fsSm, textAlign: "center" }}>
                Loading…
              </div>
            )}

            {!loading && error && (
              <div style={{ padding: "28px 16px", color: T.radarIssue, fontSize: T.fsSm }}>
                Failed to load Radar feed. {error.message}
              </div>
            )}

            {!loading && !error && allItems.length === 0 && (
              <div style={{
                padding:   "40px 16px",
                color:     T.light,
                fontSize:  T.fsSm,
                textAlign: "center",
                fontStyle: "italic",
              }}>
                {resolvedMode ? "No resolved items." : "No open items."}
              </div>
            )}

            {!loading && !error && allItems.length > 0 && BANDS.map((band) => {
              const items = bandedItems[band.key] ?? [];
              if (items.length === 0) return null;
              return (
                <div key={band.key} data-testid={`radar-band-${band.key}`} style={{ marginBottom: 4 }}>
                  {/* Band section header */}
                  <div style={{
                    display:      "flex",
                    alignItems:   "center",
                    gap:          6,
                    padding:      "7px 4px 5px",
                    position:     "sticky",
                    top:          0,
                    background:   T.white,
                    zIndex:       1,
                  }}>
                    <span style={{
                      width:        7,
                      height:       7,
                      borderRadius: "50%",
                      background:   band.dotColor,
                      flexShrink:   0,
                      display:      "inline-block",
                    }} />
                    <span style={{
                      fontSize:   T.fsXs,
                      fontWeight: T.fwBold,
                      color:      T.mid,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                      flex: 1,
                    }}>
                      {band.label}
                    </span>
                    <span style={{
                      fontSize:     T.fsXxs,
                      fontWeight:   T.fwBold,
                      background:   T.lightGray,
                      border:       `1px solid ${T.border}`,
                      borderRadius: T.radiusFull,
                      padding:      "1px 7px",
                      color:        T.mid,
                    }}>
                      {items.length}
                    </span>
                  </div>
                  {/* Items in this band */}
                  <div style={{ display: "flex", flexDirection: "column", gap: 6, paddingBottom: 6 }}>
                    {items.map((item) => (
                      <BriefCard
                        key={item.itemKey}
                        item={item}
                        impact={item.impact}
                        selected={item.itemKey === selectedKey}
                        onClick={() => handleSelect(item.itemKey)}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Rail footer */}
          {!loading && !error && (
            <div style={{
              borderTop:      `1px solid ${T.lightGray}`,
              padding:        "5px 14px",
              fontSize:       T.fsXxs,
              color:          T.light,
              display:        "flex",
              alignItems:     "center",
              justifyContent: "space-between",
              flexShrink:     0,
              background:     T.white,
            }}>
              <span>{totalCount} {totalCount === 1 ? "brief" : "briefs"}</span>
              <span style={{ fontStyle: "italic" }}>
                {resolvedMode ? "Resolution history" : "Active items"}
              </span>
            </div>
          )}
        </div>

        {/* ── Right pane: executive memo ── */}
        <BriefMemo
          item={selectedItem}
          impact={selectedItem?.impact}
          isEditor={isEditor}
          onTriage={handleTriage}
          triageError={triageError}
          onNavigate={onNavigate}
        />
      </div>
    </div>
  );
}
